/**
 * Base URL de la API (Postman: http://127.0.0.1:8000/api/v1)
 * Configurar en .env como REACT_APP_API_URL si usas otro dominio.
 */
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export const API_V1 = `${API_BASE_URL}/api/v1`;

/**
 * Origen público donde Apache/XAMPP sirve storage (sin /api).
 * Ej. XAMPP: http://localhost/chefmargaro  → imágenes en .../chefmargaro/storage/app/public/...
 */
const DEFAULT_PUBLIC_BASE =
  process.env.REACT_APP_PUBLIC_URL || 'http://localhost/chefmargaro';

function stripTrailingSlash(s) {
  return s.replace(/\/$/, '');
}

/** En XAMPP local Apache sirve storage/app/public. En producción la ruta pública es /storage/... */
function usesXamppStorageLayout(base) {
  try {
    const pub = new URL(/^https?:\/\//i.test(base) ? base : `http://${base}`);
    return pub.hostname === 'localhost' || pub.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

/**
 * Corrige URLs absolutas mal armadas (APP_URL con /api y subcarpeta del proyecto).
 * - .../api/chefmargaro/... → .../chefmargaro/... (mismo host)
 * - .../api/storage/... → REACT_APP_PUBLIC_URL + /storage/... (mismo host que la app pública)
 */
export function normalizeAbsoluteMediaUrl(url) {
  if (typeof url !== 'string') return url;
  const s = url.trim();
  try {
    const u = new URL(s);
    let { pathname } = u;
    if (/^\/api\/chefmargaro\//i.test(pathname)) {
      u.pathname = pathname.replace(/^\/api/i, '');
      return u.href;
    }
    if (/^\/api\/storage\//i.test(pathname)) {
      const publicBase = stripTrailingSlash(DEFAULT_PUBLIC_BASE);
      const pub = new URL(
        /^https?:\/\//i.test(publicBase) ? publicBase : `http://${publicBase}`
      );
      const suffix = pathname.replace(/^\/api\/storage/i, '/storage');
      const root = (pub.pathname || '/').replace(/\/$/, '') || '';
      u.protocol = pub.protocol;
      u.host = pub.host;
      u.pathname = `${root}${suffix}`.replace(/\/{2,}/g, '/');
      return u.href;
    }
  } catch {
    return s;
  }
  return s;
}

/**
 * Extrae y normaliza la parte ruta para URLs relativas al host.
 * - Quita el dominio si viene URL completa.
 * - Quita prefijo /api/ inicial para no duplicar segmentos.
 */
function extractPathPart(path) {
  if (typeof path !== 'string') return path;
  let part = path;
  if (part.includes('http://') || part.includes('https://')) {
    part = part.replace(/^.*https?:\/\/[^/]+/i, '');
  }
  if (!part.startsWith('/')) part = `/${part}`;
  part = part.replace(/^\/api\//i, '/');
  return part;
}

/**
 * Convierte ruta o URL de imagen de la API en URL absoluta para el navegador.
 * Usa REACT_APP_PUBLIC_URL (por defecto http://localhost/chefmargaro en XAMPP).
 */
export function getImageUrl(path) {
  if (!path || typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return normalizeAbsoluteMediaUrl(trimmed);
  }

  const base = stripTrailingSlash(DEFAULT_PUBLIC_BASE);
  let part = extractPathPart(trimmed);

  if (
    usesXamppStorageLayout(base) &&
    part.startsWith('/storage/') &&
    !part.startsWith('/storage/app/public/')
  ) {
    part = part.replace(/^\/storage\//, '/storage/app/public/');
  }

  if (part.startsWith('/chefmargaro/')) {
    const origin = base.replace(/\/chefmargaro$/i, '') || base;
    return origin + part;
  }

  return `${base}${part}`;
}

/**
 * Obtiene el branch_id de la sucursal seleccionada desde localStorage
 * @returns {number|null} El branch_id o null si no hay sucursal seleccionada
 */
export function getSelectedBranchId() {
  try {
    const SELECTED_BRANCH_STORAGE_KEY = 'chefmargaro_selected_branch';
    const raw = localStorage.getItem(SELECTED_BRANCH_STORAGE_KEY);
    if (!raw) return null;
    const branch = JSON.parse(raw);
    return branch.id ?? branch.branch_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Crea headers para las peticiones API incluyendo X-Branch-Id si hay una sucursal seleccionada
 * @param {object} additionalHeaders - Headers adicionales a incluir
 * @returns {object} Objeto con los headers
 */
export function getApiHeaders(additionalHeaders = {}) {
  const headers = {
    'Accept': 'application/json',
    ...additionalHeaders,
  };

  const branchId = getSelectedBranchId();
  if (branchId != null) {
    headers['X-Branch-Id'] = String(branchId);
  }

  return headers;
}

/**
 * Wrapper de fetch que automáticamente agrega el header X-Branch-Id si hay una sucursal seleccionada
 * @param {string} url - URL a la que hacer la petición
 * @param {object} options - Opciones para fetch (headers, signal, etc.)
 * @returns {Promise<Response>} Promise con la respuesta
 */
export async function fetchApi(url, options = {}) {
  // Si el body es FormData, no agregar Content-Type (el navegador lo establecerá automáticamente)
  const isFormData = options.body instanceof FormData;
  let headers;

  if (isFormData) {
    // Para FormData, solo agregar Accept y X-Branch-Id, sin Content-Type
    headers = {
      'Accept': 'application/json',
    };
    const branchId = getSelectedBranchId();
    if (branchId != null) {
      headers['X-Branch-Id'] = String(branchId);
    }
    // Si hay headers adicionales, agregarlos (excepto Content-Type)
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        if (key.toLowerCase() !== 'content-type') {
          headers[key] = options.headers[key];
        }
      });
    }
  } else {
    headers = getApiHeaders(options.headers);
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Wrapper de fetch que NO incluye el header X-Branch-Id
 * Útil para obtener todas las sucursales disponibles sin filtrar
 * @param {string} url - URL a la que hacer la petición
 * @param {object} options - Opciones para fetch (headers, signal, etc.)
 * @returns {Promise<Response>} Promise con la respuesta
 */
export async function fetchApiWithoutBranch(url, options = {}) {
  const headers = {
    'Accept': 'application/json',
    ...options.headers,
  };
  return fetch(url, {
    ...options,
    headers,
  });
}
